import { AbstractMesh, Mesh, MeshBuilder, Vector3 } from '@babylonjs/core';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { Level } from '../Level';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

/**
 * Mimic Behavior – disguises itself as an idle collectible-like object.
 * When the player gets close, it reveals itself and switches to chase behavior.
 *
 * Config fields:
 *   detectionRange  – radius at which mimic reveals (default 6)
 *   speed           – chase speed after reveal (default 4)
 */
export class EnemyMimicBehavior extends EnemyBehavior {
  private revealed: boolean = false;
  private bobTime: number = 0;
  private baseY: number = 0;
  private chasing: boolean = false;
  private originalScale: Vector3 = Vector3.Zero();
  private mimic: Mesh;

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    super('enemyMimic', startPosition, config);
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);
    this.baseY = mesh.position.y;
  }

  initLevel(level: Level): EnemyMimicBehavior {
    super.initLevel(level);
    this.originalScale.copyFrom(this.mesh.scaling);
    this.mesh.scaling = this.mesh.scaling.divide(new Vector3(3, 3, 3));
    this.mimic = this.createMimic();
    return this;
  }

  behaviorUpdate(deltaTime: number): void {
    if (!this.level?.player) return;

    if (!this.revealed) {
      // Disguise: gentle bobbing float
      this.bobTime += deltaTime;
      this.mesh.position.y = this.baseY + 1.5 + Math.sin(this.bobTime * 2) * 0.3;
      this.mesh.rotation.y += deltaTime * 0.8;

      const dist = this.getPlayerDistance();
      if (dist <= (this.config.detectionRange ?? 6)) {
        this.reveal();
      }
    } else {
      // Chase behavior after reveal
      const dist = this.getPlayerDistance();
      if (dist <= (this.config.detectionRange ?? 6) * 3) {
        this.chasing = true;
        this.playAnimation(ENEMY_ANIMATIONS.SPRINT, true, 1.5);
        this.moveToward(this.getPlayerPosition(), deltaTime * 1.5);
      } else if (this.chasing) {
        // Return to start
        const distToStart = Vector3.Distance(this.mesh.position, this.startPosition);
        if (distToStart > 0.5) {
          this.playAnimation(ENEMY_ANIMATIONS.WALK, true);
          this.moveToward(this.startPosition, deltaTime);
        } else {
          this.mesh.position.copyFrom(this.startPosition);
          this.snapToGround(deltaTime);
          this.chasing = false;
          this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        }
      } else {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
      }
    }
  }

  private reveal(): void {
    if (!this.level || this.revealed) return;
    this.revealed = true;
    this.level.particles.createShieldBurstEffect(this.mesh.position.clone());
    this.mimic.dispose();
    this.mimic = undefined;
    this.mesh.scaling.copyFrom(this.originalScale);
    // Snap back to ground, stop bobbing
    this.mesh.position.y = this.baseY;
    this.snapToGround();

    // Reveal particle burst
    this.level.particles.createDisappearanceParticleSystem(this.mesh as import('@babylonjs/core').AbstractMesh, 0.5);
    this.level.game.sound.play('enemydestroy', this.mesh);

    // Start chasing immediately
    this.chasing = true;
    this.playAnimation(ENEMY_ANIMATIONS.SPRINT, true, 1.5);
  }

  private createMimic(): Mesh {
    const mimic = MeshBuilder.CreateSphere('mimicSphere', { diameter: 2.5, segments: 32 }, this.level.scene);
    mimic.parent = this.mesh;
    mimic.position.y = 0; // Center on player
    mimic.material = this.level.game.materialService.createSimpleGlassMaterial();
    mimic.isPickable = false;
    return mimic;
  }

  clone(): EnemyMimicBehavior {
    return new EnemyMimicBehavior(this.startPosition, this.config);
  }
}
